import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, SlidersHorizontal, Target } from 'lucide-react';
import { ApiErrorModal } from '@components/ApiErrorModal';
import { Switch } from '@components/Switch';
import settingsService from '@services/settingsService';
import type { AppSettings } from '@/types/planning';
import { formatDateTimeBR } from '@/utils/formatters';

/**
 * Administração → Configurações (só admin): liberação geral do Acervo CAC.
 * Regra do backend: admin sempre tem; os demais têm se a liberação geral OU a individual estiver ligada.
 */
export const SettingsAdminView: React.FC = () => {
  const [settings, setSettings] = useState<AppSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setSettings(await settingsService.get());
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const toggleCac = async (value: boolean) => {
    setSaving(true);
    try {
      setSettings(await settingsService.update(value));
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4 max-w-3xl">
      <h1 className="text-h3 flex items-center gap-2">
        <SlidersHorizontal size={20} className="text-slate-500" /> Configurações
      </h1>
      <p className="text-small">Ajustes globais da aplicação. Só administradores veem esta tela.</p>

      {loading && !settings ? (
        <p className="text-center text-slate-400 py-6">
          <Loader2 size={18} className="inline animate-spin mr-2" /> Carregando…
        </p>
      ) : (
        <div className="card p-4 sm:p-5 space-y-4">
          <div className="flex items-start gap-3">
            <span className="p-2 rounded-lg bg-emerald-50 text-emerald-700 flex-shrink-0">
              <Target size={18} />
            </span>
            <div className="min-w-0 flex-1 space-y-3">
              <div>
                <h2 className="text-base font-semibold text-slate-800">Acervo CAC</h2>
                <p className="text-small mt-1">
                  O módulo Acervo CAC (armas, CR/CRAF/GT e vencimentos) só aparece para quem está liberado. O
                  administrador sempre tem acesso; os demais usuários têm quando a liberação geral abaixo está ligada
                  <strong> ou</strong> quando a liberação individual está marcada em{' '}
                  <a href="/admin/users" className="text-emerald-700 hover:underline">
                    Administração → Usuários
                  </a>
                  .
                </p>
              </div>
              <Switch
                checked={!!settings?.cac_enabled_for_all}
                onChange={toggleCac}
                loading={saving}
                label={<span className="font-medium">Liberar Acervo CAC para todos os usuários</span>}
              />
              <p className="text-small">
                {settings?.cac_enabled_for_all
                  ? 'Ligado: todos os usuários ativos veem o Acervo CAC, independentemente da liberação individual.'
                  : 'Desligado: só o administrador e os usuários com liberação individual veem o Acervo CAC.'}
                {settings?.updated_at && (
                  <>
                    {' '}
                    Última alteração em {formatDateTimeBR(settings.updated_at)}
                    {settings.updated_by ? ` por ${settings.updated_by}` : ''}.
                  </>
                )}
              </p>
            </div>
          </div>
        </div>
      )}

      <ApiErrorModal error={error} onClose={() => setError(null)} />
    </div>
  );
};
